import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { tasksApi, type TaskAnswer } from './tasks'
import { queryKeys } from './query-keys'

/**
 * The team's task list (mock-only endpoint). Polls on the same 20 s beat as
 * the activity feed so a colleague's answer takes a task off your list
 * without a reload — two people calling the same customer is the exact
 * failure this screen exists to prevent.
 */
export function useTasks(teamId: string | number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.list(teamId ?? 'none'),
    // The list is the slowest read on the screen. Pass the signal so an
    // invalidation aborts an in-flight poll instead of letting its older
    // rows land after the fresh ones.
    queryFn: ({ signal }) => tasksApi.list(Number(teamId), signal),
    enabled: !!teamId,
    refetchInterval: 20 * 1000,
  })
}

/** One estimate's task history — the timeline on the detail page. */
export function useEstimateTasks(estimateId: string | number | null | undefined) {
  return useQuery({
    queryKey: queryKeys.tasks.byEstimate(estimateId ?? 'none'),
    queryFn: () => tasksApi.byEstimate(Number(estimateId)),
    enabled: !!estimateId,
  })
}

// An answer changes the estimate (status, head), the list, the bell (a
// decline writes a status event) and the inbox (a callback pauses the drip),
// so every one of those caches has to hear about it or two screens disagree.
export function useInvalidateTaskWorld() {
  const qc = useQueryClient()
  return (estimateId?: number) => {
    qc.invalidateQueries({ queryKey: queryKeys.tasks.all })
    qc.invalidateQueries({ queryKey: queryKeys.estimates.all })
    qc.invalidateQueries({ queryKey: queryKeys.activity.all })
    qc.invalidateQueries({ queryKey: queryKeys.campaigns.all })
    qc.invalidateQueries({ queryKey: queryKeys.customerActivity.all })
    if (estimateId != null) {
      qc.invalidateQueries({ queryKey: queryKeys.tasks.byEstimate(estimateId) })
    }
  }
}

export function useAnswerTask() {
  const invalidate = useInvalidateTaskWorld()
  return useMutation({
    mutationFn: ({
      taskId,
      answer,
      note,
    }: {
      taskId: number
      answer: TaskAnswer
      note?: string | null
      estimateId: number
    }) => tasksApi.answer(taskId, answer, note),
    onSuccess: (_data, vars) => invalidate(vars.estimateId),
  })
}

export function useSkipTask() {
  const invalidate = useInvalidateTaskWorld()
  return useMutation({
    mutationFn: ({ taskId }: { taskId: number; estimateId: number }) => tasksApi.skip(taskId),
    onSuccess: (_data, vars) => invalidate(vars.estimateId),
  })
}

export function useSnoozeEstimate() {
  const invalidate = useInvalidateTaskWorld()
  return useMutation({
    mutationFn: ({
      estimateId,
      action,
      until,
      note,
    }: {
      estimateId: number
      action: 'snooze' | 'wake'
      until?: string | null
      note?: string
    }) => tasksApi.snooze(estimateId, action, until, note),
    onSuccess: (_data, vars) => invalidate(vars.estimateId),
  })
}
